import React, { useEffect, useRef, useState } from 'react';
import type { UtPlayerInfo } from '../../../hooks/useUtPlayerIndex';
import { formatXp } from '../format';
import { Pitch, TeamVsBar, TEAM_COLOR } from '../gameParts';
import { Eyebrow, Panel, PlayerRow, UtButton, UT_LOGO } from '../primitives';
import { UtTeamPack, hasSeenPack, markPackSeen } from '../UtTeamPack';
import type { UtCardPlayer } from '../UtCard';
import { kickOff, shortDate, shortDateTime } from './dates';

export interface LineupRegistration {
  playerId: string;
  name: string;
  status: string;
  team: 'blue' | 'orange' | null;
}

interface Props {
  gameId: string;
  matchNumber: number | string;
  date: string;
  venueName?: string | null;
  announcedAt?: string | null;
  registrations: LineupRegistration[];
  index: Map<string, UtPlayerInfo>;
  playerId: string | null;
}

const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

/**
 * Teams announced: header with the pack button, Blue vs Orange bar, "you're
 * on …", the pitch, a public-stats comparison (never skill ratings) and
 * reserves. The team pack opens by itself the first time per game.
 */
export const Lineups: React.FC<Props> = ({ gameId, matchNumber, date, venueName, announcedAt, registrations, index, playerId }) => {
  const toCard = (r: LineupRegistration): UtCardPlayer & { info?: UtPlayerInfo } => {
    const info = index.get(r.playerId);
    return { id: r.playerId, name: r.name, xp: info?.xp ?? 0, rarity: info?.rarity, position: info?.position, whatsappMember: info?.whatsappMember, photo: info?.photo, info };
  };
  const byXp = (a: UtCardPlayer, b: UtCardPlayer) => b.xp - a.xp;
  const blue = registrations.filter((r) => r.status === 'selected' && r.team === 'blue').map(toCard).sort(byXp);
  const orange = registrations.filter((r) => r.status === 'selected' && r.team === 'orange').map(toCard).sort(byXp);
  const reserves = registrations.filter((r) => r.status === 'reserve').map(toCard).sort(byXp);
  const myTeam = registrations.find((r) => r.playerId === playerId && r.status === 'selected')?.team ?? null;
  const myPlayers = myTeam === 'blue' ? blue : myTeam === 'orange' ? orange : [];

  const [packOpen, setPackOpen] = useState(false);
  const pitchRef = useRef<HTMLDivElement>(null);

  // First visit after teams are announced: show the pack once.
  useEffect(() => {
    if (myTeam && myPlayers.length > 0 && !hasSeenPack(gameId)) {
      markPackSeen(gameId);
      setPackOpen(true);
    }
    // Only when the team first becomes known for this game.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, myTeam, myPlayers.length > 0]);

  const teamStats = (list: typeof blue) => {
    const infos = list.map((p) => p.info).filter((i): i is UtPlayerInfo => !!i);
    const rated = infos.filter((i) => i.winRate != null && i.caps >= 10);
    return {
      xp: Math.round(avg(list.map((p) => p.xp))),
      wr: rated.length ? `${Math.round(avg(rated.map((i) => i.winRate as number)))}%` : '—',
      caps: Math.round(avg(infos.map((i) => i.caps))),
      streak: avg(infos.map((i) => i.streak)).toFixed(1),
      motm: infos.reduce((t, i) => t + i.motm, 0),
    };
  };
  const bs = teamStats(blue);
  const os = teamStats(orange);
  const compare: [string, string | number, string | number][] = [
    ['WIN RATE', bs.wr, os.wr],
    ['AVG CAPS', bs.caps, os.caps],
    ['AVG STREAK', bs.streak, os.streak],
    ['MOTMS', bs.motm, os.motm],
  ];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Eyebrow>TEAMS ANNOUNCED{announcedAt ? ` · ${shortDateTime(announcedAt)}` : ''}</Eyebrow>
          <h1 className="font-ut-display text-[34px] font-extrabold uppercase leading-none">Match #{matchNumber} line-ups</h1>
          <span className="text-[13px] text-ut-mut">
            {shortDate(date)} · {kickOff(date)}
            {venueName ? ` · ${venueName}` : ''}
          </span>
        </div>
        {myTeam && (
          <UtButton variant="pack" onClick={() => setPackOpen(true)} className="!py-2.5 !pl-2.5 !pr-3.5">
            <span
              className="flex h-9 w-[26px] items-center justify-center bg-[#1a1204]"
              style={{ clipPath: 'polygon(0 8%, 50% 0, 100% 8%, 100% 92%, 50% 100%, 0 92%)' }}
            >
              <img src={UT_LOGO} alt="" className="w-[18px]" />
            </span>
            <span className="text-[17px]">OPEN TEAM PACK</span>
          </UtButton>
        )}
      </div>

      <TeamVsBar blueSub={`Avg ${formatXp(bs.xp)} XP · WR ${bs.wr}`} orangeSub={`Avg ${formatXp(os.xp)} XP · WR ${os.wr}`} />

      {myTeam && (
        <Panel tone="accent" pad="none" className="rounded-[10px] px-3.5 py-2.5 text-sm font-semibold">
          You&apos;re on{' '}
          <span style={{ color: TEAM_COLOR[myTeam] }} className="capitalize">
            {myTeam}
          </span>
          .
        </Panel>
      )}

      <div ref={pitchRef}>
        <Pitch
          halves={[
            { name: 'BLUE', players: blue },
            { name: 'ORANGE', players: orange },
          ]}
          highlightId={playerId}
        />
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] gap-3.5">
        <div className="grid grid-cols-[1fr_auto_1fr] content-start gap-x-3 gap-y-1.5 rounded-xl bg-ut-card p-3.5 text-sm">
          {compare.map(([k, b, o]) => (
            <React.Fragment key={k}>
              <span className="font-ut-display text-xl font-extrabold text-ut-blue">{b}</span>
              <span className="self-center text-center text-xs tracking-[0.1em] text-ut-mut">{k}</span>
              <span className="text-right font-ut-display text-xl font-extrabold text-ut-orange">{o}</span>
            </React.Fragment>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <span className="font-ut-display text-xl font-extrabold uppercase">Reserves</span>
          {reserves.length === 0 && <span className="text-sm text-ut-mut">No reserves this week.</span>}
          {reserves.map((p) => (
            <div key={p.id} className="overflow-hidden rounded-[10px] bg-ut-card">
              <PlayerRow
                to={`/players/${p.id}`}
                playerId={p.id}
                name={p.name}
                rarity={p.rarity}
                xp={p.xp}
                right={
                  <span className="text-[13px] text-ut-mut">
                    {p.position ? `${p.position} · ` : ''}
                    {formatXp(p.xp)} XP
                  </span>
                }
              />
            </div>
          ))}
        </div>
      </div>

      {myTeam && (
        <UtTeamPack
          open={packOpen}
          onClose={() => setPackOpen(false)}
          onSeeLineups={() => {
            setPackOpen(false);
            pitchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          team={myTeam}
          players={myPlayers}
          matchNumber={matchNumber}
        />
      )}
    </section>
  );
};
